import { groupBy } from 'lodash-es';
import Vue from 'vue';
import api from '../api';
import router from '../router';

export default {
    namespaced: true,
    state: {
        authenticated: false,
        confirmed: null,
        user: null,
        passwordRegex: /(?=^.{8,}$)((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/
    },
    mutations: {
        authenticated(state, value) {
            state.authenticated = !!value;
        },
        confirmed(state, value) {
            state.confirmed = !!value;
        },
        user(state, value) {
            state.user = value;
        },
        orders(state, value) {
            if (state.user) {
                state.user.orders = value;
            }
        },
        removeNotification(state, index) {
            if (state.user.notifications && state.user.notifications.index) {
                state.user.notifications.splice(index, 1);
            }
        }
    },
    getters: {
        authenticated(state) {
            return state.authenticated;
        },
        addresses(state) {
            try {
                return state.user.addresses.map(({ id, display_full }) => ({
                    id,
                    label: display_full
                }));
            }
            catch (e) {
                return [];
            }
        },
        confirmed(state) {
            return state.confirmed;
        },
        events(state) {
            try {
                return state.user.properties.map(property => property.events).reduce((acc, cur) => [...acc, ...cur]);
            }
            catch (e) {
                return [];
            }
        },
        solicitors(state) {
            try {
                return state.user.solicitors.map(solicitor => ({
                    id: solicitor.id,
                    name: solicitor.name_company
                }));
            }
            catch (e) {
                return [];
            }
        },
        sortedEvents(state, getters) {
            try {
                return groupBy(getters.events, event => event.status.name);
            }
            catch (e) {
                return {};
            }
        },
        notificationCounts(state) {
            return {
                alerts: (state.user && state.user.notifications && state.user.notifications.filter(item => !item.read_at).length) || 0,
                messages: (state.user && state.user.unread_messages) || 0,
                account: (state.user && state.user.notifications && state.user.notifications.filter(item => item.category && item.category.toLowerCase() === 'account').length) || 0
            };
        }
    },
    actions: {
        checkAuthentication({ commit, getters, state }, { requiresAuth = true, enforceRedirect = true } = {}) {
            return new Promise((resolve) => {
                if (getters.authenticated || requiresAuth === false) {
                    resolve(true);
                }
                else {
                    api.get('user')
                        .then((response) => {
                            commit('user', response.data.payload.user);
                            commit('authenticated', true);
                            resolve(true);
                        })
                        .catch(() => {
                            commit('authenticated', false);
                            if (enforceRedirect) {
                                router.push({ name: 'login', query: { redirect: router.currentRoute.fullPath } });
                            }
                            resolve(false);
                        });
                }
            });
        },
        confirm({ commit }, code) {
            api.patch('user/confirm', { code })
                .then(() => {
                    commit('confirmed', true);
                })
                .catch(() => {
                    commit('confirmed', false);
                });
        },
        confirmEvent({ dispatch }, data) {
            api.patch(`events/${data.id}`, { action: 'approve', ...data })
                .then((response) => {
                    Vue.toasted.success('The event has been successfully approved.');
                    dispatch('refreshUser');
                    return response;
                })
                .catch(() => {
                    Vue.toasted.error('There was an error while attempting to approve the event.');
                });
        },
        declineEvent({ dispatch }, data) {
            api.patch(`events/${data.id}`, { action: 'decline', ...data })
                .then((response) => {
                    Vue.toasted.success('The event request has been successfully decline.');
                    dispatch('refreshUser');
                    return response;
                })
                .catch(() => {
                    Vue.toasted.error('There was an error while attempting to decline the event request.');
                });
        },
        cancelEvent({ dispatch }, data) {
            api.patch(`events/${data.id}`, { action: 'cancel', ...data })
                .then((response) => {
                    Vue.toasted.success('The event has been successfully cancelled.');
                    dispatch('refreshUser');
                    return response;
                })
                .catch(() => {
                    Vue.toasted.error('There was an error while attempting to cancel the event.');
                });
        },
        login({ commit }, data) {
            return api.post('login', data)
                .then((response) => {
                    const { user, confirmed = false } = response.data.payload;
                    commit('user', user);
                    commit('authenticated', true);
                    if (confirmed) {
                        Vue.toasted.success('Your account has now been confirmed');
                    }
                    router.push(data.redirect || { name: 'dashboard' });
                    return response;
                })
                .catch(({ response }) => response);
        },
        logout({ commit }, redirect = '/') {
            api.post('logout')
                .then(() => {
                    commit('authenticated', false);

                    if (redirect) {
                        router.push(redirect);
                    }
                })
                .catch(() => {
                    commit('authenticated', false);
                });
        },
        passwordResetChange(context, user) {
            return api.patch('user/password-reset', {
                password: user.password,
                password_confirmation: user.password_confirmation,
                token: user.token
            });
        },
        passwordResetRequest(context, { email }) {
            return api.post('user/password-reset/request', { email });
        },
        refreshUser({ commit }) {
            return api.get('user')
                .then((response) => {
                    commit('user', response.data.payload.user);
                    commit('authenticated', true);
                })
                .catch(() => {
                    commit('authenticated', false);
                    router.push({ name: 'login', query: { redirect: router.currentRoute.fullPath } });
                });
        },
        register(context, data) {
            return api.post('register', data);
        },
        submitAmlCheck() {
            return api.post('user/aml');
        },
        toggleBookmark({ dispatch }, id) {
            return api.patch('user/bookmarks', { id })
                .then(() => {
                    dispatch('refreshUser');
                    Vue.toasted.success('Saved properties updated');
                })
                .catch(() => {
                    Vue.toasted.error('Unable to update bookmarks');
                });
        },
        updateProfile(context, data) {
            return api.patch('user', data);
        },
        deleteAccount({ commit }, data) {
            return api.delete('user')
                .then(() => {
                    commit('authenticated', false);
                    router.push({ name: 'login' });
                    Vue.toasted.success('Your account has been successfully deleted');
                })
                .catch(() => {
                    Vue.toasted.error('There was an error while attempting to delete your account');
                });
        },
        getOrders({ commit }, page = 1) {
            return api.get(`user/orders?page=${page}`)
                .then(({ data }) => {
                    commit('orders', data.payload.orders);
                })
                .catch(() => {
                    Vue.toasted.error('There was an error while attempting to fetch orders');
                });
        },
        resendConfirmationEmail(context, data) {
            return api.post(`user/resend-validation-email`, data);
        }
    }
};
